import {
  Button, Tooltip, message, Breadcrumb, Input, Tag, Select, Dropdown,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, SearchOutlined, UserSwitchOutlined, CloseCircleOutlined, StopOutlined,
  MoreOutlined, EyeOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { referralApi } from '../../../api/referral.api';
import DataTable from '../../../components/common/DataTable';
import ReferralForm from '../components/ReferralForm';
import AssignDoctorModal from '../components/AssignDoctorModal';
import RejectReferralModal from '../components/RejectReferralModal';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import useDebounce from '../../../hooks/useDebounce';
import { usePermission } from '../../../hooks/usePermission';
import { useAuthStore } from '../../../store/auth.store';
import { getApiErrorMessage, formatDate, calculateAge } from '../../../utils/helpers';

const buildFormData = (values) => {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (key === 'files') { return; }
    if (value !== undefined && value !== null && value !== '') { formData.append(key, value); }
  });
  (values.files || []).forEach((file) => formData.append('attachments', file));
  return formData;
};

const ReferralList = () => {
  const [formOpen,    setFormOpen]    = useState(false);
  const [editRecord,  setEditRecord]  = useState(null);
  const [assignOpen,  setAssignOpen]  = useState(false);
  const [assignRecord, setAssignRecord] = useState(null);
  const [rejectOpen,  setRejectOpen]  = useState(false);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [pagination,  setPagination]  = useState({ page: 1, limit: 10 });
  const [sort,        setSort]        = useState({ sortBy: 'createdAt', sortOrder: 'desc' });

  const queryClient = useQueryClient();
  const navigate     = useNavigate();
  const { isAdmin, isReferralDoctor } = usePermission();
  const { user } = useAuthStore();
  const currentDoctorId = user?.doctor?.toString?.() || user?.doctor;

  const debouncedSearch = useDebounce(searchInput, 300);

  const { data, isLoading } = useQuery({
    queryKey: ['referrals', debouncedSearch, assignmentFilter, sort, pagination],
    queryFn:  () => referralApi.getAll({
      search: debouncedSearch,
      ...(assignmentFilter ? { assignmentStatus: assignmentFilter } : {}),
      ...sort,
      ...pagination,
    }),
  });

  const createMutation = useMutation({
    mutationFn: referralApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['referrals']);
      message.success('Child referred successfully');
      setFormOpen(false);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => referralApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['referrals']);
      message.success('Referral updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: referralApi.delete,
    onSuccess: () => { queryClient.invalidateQueries(['referrals']); message.success('Referral deleted'); },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, doctorId }) => referralApi.assign(id, doctorId),
    onSuccess: () => {
      queryClient.invalidateQueries(['referrals']);
      message.success('Referral assigned — the doctor has been notified by email');
      setAssignOpen(false);
      setAssignRecord(null);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => referralApi.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(['referrals']);
      message.success('Referral rejected — the referring doctor has been notified by email');
      setRejectOpen(false);
      setRejectRecord(null);
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const handleFormSubmit = (values) => {
    const formData = buildFormData(values);
    if (editRecord) { updateMutation.mutate({ id: editRecord.id, data: formData }); }
    else            { createMutation.mutate(formData); }
  };

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleTableChange = useCallback((newPagination, _filters, sorter) => {
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setSort(nextSorter?.order
      ? {
          sortBy:    nextSorter.field || nextSorter.columnKey,
          sortOrder: nextSorter.order === 'ascend' ? 'asc' : 'desc',
        }
      : { sortBy: 'createdAt', sortOrder: 'desc' });
    setPagination({ page: newPagination.current || 1, limit: newPagination.pageSize || 10 });
  }, []);

  const allReferrals = data?.data || [];
  const meta         = data?.meta || {};

  const getSortOrder = (field) => (
    sort.sortBy === field ? (sort.sortOrder === 'asc' ? 'ascend' : 'descend') : null
  );

  const columns = [
    {
      title: 'Child / Patient Name', dataIndex: 'childName', key: 'childName', ellipsis: true,
      sorter: true, sortOrder: getSortOrder('childName'),
    },
    {
      title: 'Age', key: 'age', width: 80,
      render: (_, record) => {
        const age = calculateAge(record.childDob);
        return age === '-' ? '-' : `${age} yrs`;
      },
    },
    {
      title: 'Parent / Guardian', dataIndex: 'parentName', key: 'parentName', ellipsis: true,
      sorter: true, sortOrder: getSortOrder('parentName'),
    },
    {
      title: 'Parent Contact', dataIndex: 'parentContact', key: 'parentContact', width: 140,
    },
    {
      title: 'Target Clinic', key: 'targetClinic', ellipsis: true,
      render: (_, record) => record.targetClinic?.name || '-',
    },
    {
      title: 'Referring Doctor', key: 'referringDoctor', ellipsis: true,
      render: (_, record) => record.referringDoctor?.name || '-',
    },
    {
      title: 'Assigned Doctor', key: 'assignedDoctor', ellipsis: true,
      render: (_, record) => (record.assignedDoctor?.name
        ? record.assignedDoctor.name
        : <Tag style={{ borderRadius: 9999, fontSize: 11 }}>Unassigned</Tag>),
    },
    {
      title: 'Status', key: 'status', width: 110,
      render: (_, record) => {
        if (record.status === 'rejected') {
          return (
            <Tooltip title={record.rejectionReason || 'Rejected'}>
              <Tag color="error" style={{ borderRadius: 9999, fontSize: 11 }}>Rejected</Tag>
            </Tooltip>
          );
        }
        if (record.status === 'accepted') {
          return <Tag color="success" style={{ borderRadius: 9999, fontSize: 11 }}>Accepted</Tag>;
        }
        return <Tag style={{ borderRadius: 9999, fontSize: 11 }}>Pending</Tag>;
      },
    },
    {
      title: 'Referred On', dataIndex: 'createdAt', key: 'createdAt', width: 130,
      sorter: true, sortOrder: getSortOrder('createdAt'),
      render: (v) => formatDate(v),
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 80, align: 'center',
      render: (_, record) => {
        const ownsRecord = isAdmin || record.referringDoctor?.id === currentDoctorId || record.referringDoctor?._id === currentDoctorId;
        const isRejected = record.status === 'rejected';

        const items = [
          {
            key: 'view',
            label: 'View',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/referrals/${record.id}`),
          },
          isAdmin && {
            key: 'assign',
            label: isRejected ? 'Approve & Assign Doctor' : 'Assign to Doctor',
            icon: isRejected ? <CheckCircleOutlined /> : <UserSwitchOutlined />,
            onClick: () => { setAssignRecord(record); setAssignOpen(true); },
          },
          isAdmin && !isRejected && {
            key: 'reject',
            label: 'Reject Referral',
            icon: <StopOutlined />,
            danger: true,
            onClick: () => { setRejectRecord(record); setRejectOpen(true); },
          },
          ownsRecord && { key: 'edit-divider', type: 'divider' },
          ownsRecord && {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            onClick: () => { setEditRecord(record); setFormOpen(true); },
          },
          ownsRecord && {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => showDeleteConfirm({
              content: 'Are you sure you want to delete this referral?',
              onOk: () => deleteMutation.mutate(record.id),
            }),
          },
        ].filter(Boolean);

        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button shape="circle" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Referred Children' },
        ]}
      />

      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        marginBottom: 16,
        background:   '#fff',
        borderRadius: 14,
        padding:      '10px 16px',
        boxShadow:    '0 2px 8px rgba(var(--wl-primary-rgb),0.08)',
        border:       '1px solid var(--wl-primary-pale)',
      }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by child, parent, contact…"
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{ flex: 1, maxWidth: 340, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
        />
        <Select
          value={assignmentFilter}
          onChange={(val) => { setAssignmentFilter(val); setPagination((p) => ({ ...p, page: 1 })); }}
          allowClear={!!assignmentFilter}
          onClear={() => { setAssignmentFilter(''); setPagination((p) => ({ ...p, page: 1 })); }}
          clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
          style={{ width: 160 }}
          options={[
            { label: 'All Referrals', value: '' },
            { label: 'Unassigned',    value: 'unassigned' },
            { label: 'Assigned',      value: 'assigned' },
          ]}
        />
        {isReferralDoctor && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginLeft: 'auto' }}
            onClick={() => { setEditRecord(null); setFormOpen(true); }}
          >
            Refer a Child
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        dataSource={allReferrals}
        loading={isLoading}
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 1150 }}
        pagination={{
          current:  pagination.page,
          pageSize: pagination.limit,
          total:    meta.total,
        }}
        onChange={handleTableChange}
      />

      <ReferralForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <AssignDoctorModal
        open={assignOpen}
        onClose={() => { setAssignOpen(false); setAssignRecord(null); }}
        onSubmit={(doctorId) => assignMutation.mutate({ id: assignRecord.id, doctorId })}
        record={assignRecord}
        loading={assignMutation.isPending}
      />

      <RejectReferralModal
        open={rejectOpen}
        onClose={() => { setRejectOpen(false); setRejectRecord(null); }}
        onSubmit={(reason) => rejectMutation.mutate({ id: rejectRecord.id, reason })}
        record={rejectRecord}
        loading={rejectMutation.isPending}
      />
    </div>
  );
};

export default ReferralList;
